import { useEffect, useMemo, useState } from 'react'
import { useSetAtom } from 'jotai'
import { PartnerService, UserService } from '@duck/api-definitions'
import { invitationDataAtom, assignmentTokenAtom } from '@/store/userAtoms'
import { SignupForm } from '@/components/SignupDialog/SignupForm'
import { GoogleSignInButton } from '@/components/LoginDialog/GoogleSignInButton'
import { LinkedInSignInButton } from '@/components/LoginDialog/LinkedInSignInButton'
import { useAuth } from '@/hooks/useAuth'
import { useTar } from '@/modules'
import { Button } from '@duck/ui'
import { InviteHeroPartner } from './InviteHeroPartner'
import { InviteHeroReferral } from './InviteHeroReferral'
import { InviteHeroCandidate } from './InviteHeroCandidate'

interface InviteGateSignupProps {
  details: UserService.InvitationDetails
  token: string
}

// Phase 4 — keep it simple: push the fetched invitation into the existing
// `invitationDataAtom`/`assignmentTokenAtom` so SignupForm + social buttons
// "just work" via useAuth. Phase 5 will lift these out of the dialog atoms.
export const InviteGateSignup = ({ details, token }: InviteGateSignupProps) => {
  const { t } = useTar()
  const setInvitationData = useSetAtom(invitationDataAtom)
  const setAssignmentToken = useSetAtom(assignmentTokenAtom)
  const { signInWithGoogle, signInWithLinkedIn, error } = useAuth()
  const [showGoogleConfirm, setShowGoogleConfirm] = useState(false)
  const [showLinkedInConfirm, setShowLinkedInConfirm] = useState(false)
  const [showEmail, setShowEmail] = useState(false)

  useEffect(() => {
    setInvitationData(details)
    setAssignmentToken(token)
    return () => {
      setInvitationData(null)
      setAssignmentToken(null)
    }
  }, [details, token, setInvitationData, setAssignmentToken])

  const isPartnerInvite = details.role === UserService.UserRole.Partner
  const isCandidateInvite =
    !isPartnerInvite &&
    details.role !== UserService.UserRole.Admin &&
    !!details.organizationName &&
    !details.inviterName

  const headline = useMemo(() => {
    if (details.role === UserService.UserRole.Admin) return t('invite.signup.headlineAdmin')
    if (details.inviterName) {
      return t('invite.signup.headlineReferral', { inviter: details.inviterName })
    }
    return t('invite.signup.headlineGeneric')
  }, [details, t])

  return (
    <div className="w-full max-w-md space-y-6">
      {isPartnerInvite ? (
        <InviteHeroPartner
          organizationName={details.organizationName ?? ''}
          inviterName={details.inviterName ?? undefined}
          logoSquareURL={details.organizationLogoSquareURL ?? undefined}
        />
      ) : isCandidateInvite ? (
        <InviteHeroCandidate
          organizationName={details.organizationName ?? ''}
          logoURL={details.organizationLogoURL ?? undefined}
          logoCanvas={details.organizationLogoCanvas as PartnerService.OrganizationLogoCanvas | undefined}
        />
      ) : (
        <InviteHeroReferral
          headline={headline}
          subtitle={t('invite.signup.subtitle')}
          inviterName={details.inviterName ?? undefined}
        />
      )}

      {details.message && !isCandidateInvite && (
        <blockquote className="relative bg-card/60 rounded-lg ps-9 pe-4 py-4 text-sm italic whitespace-pre-line text-foreground">
          <span
            aria-hidden
            className="absolute top-0 start-2 text-5xl leading-none text-accent/40 font-serif select-none"
          >
            &ldquo;
          </span>
          {details.message}
          {details.inviterName && (
            <footer className="mt-3 not-italic text-xs text-muted-foreground">
              &mdash; {details.inviterName}
            </footer>
          )}
        </blockquote>
      )}

      {details.email && (
        <div className="bg-success/10 border border-success/20 rounded-lg p-3 text-sm text-center">
          {t('invite.signup.emailLockedHint', { email: details.email })}
        </div>
      )}

      {!showEmail && (
        <div className="space-y-3">
          <div className="flex flex-col">
            <LinkedInSignInButton
              onClick={() => {
                setShowGoogleConfirm(false)
                setShowLinkedInConfirm(true)
              }}
              isDisabled={false}
            />
            {showLinkedInConfirm && details.email && (
              <div
                data-testid="invite-linkedin-confirm"
                className="mt-2 bg-card border border-border rounded-lg p-3 text-sm"
              >
                <p className="mb-2">
                  {t('auth.registrationViaLinkedInMustBeWithAccountLinkedToEmail')}{' '}
                  <span className="font-mono font-semibold text-accent">{details.email}</span>
                </p>
                <div className="flex justify-center">
                  <Button variant="outline" size="sm" onClick={() => signInWithLinkedIn?.()}>
                    {t('common.continue')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <GoogleSignInButton
              onClick={() => {
                setShowLinkedInConfirm(false)
                setShowGoogleConfirm(true)
              }}
              isDisabled={false}
            />
            {showGoogleConfirm && details.email && (
              <div
                data-testid="invite-google-confirm"
                className="mt-2 bg-card border border-border rounded-lg p-3 text-sm"
              >
                <p className="mb-2">
                  {t('auth.registrationViaGoogleMustBeWithAccountLinkedToEmail')}{' '}
                  <span className="font-mono font-semibold text-accent">{details.email}</span>
                </p>
                <div className="flex justify-center">
                  <Button variant="outline" size="sm" onClick={() => signInWithGoogle?.()}>
                    {t('common.continue')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3 text-sm text-error">
          {error}
        </div>
      )}

      {!showEmail && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setShowEmail(true)}
            className="text-sm text-accent hover:text-foreground"
          >
            {t('auth.continueWithEmail')}
          </button>
        </div>
      )}

      {showEmail && <SignupForm />}
    </div>
  )
}
